<template>
  <HrPageLayout
    title="Training Calendar"
    subtitle="Scheduled training sessions by date"
    icon="📅"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/dashboard' },
      { label: 'Training & Development' },
      { label: 'Training Calendar' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-secondary" @click="refreshData">
        <i class="bx bx-refresh" :class="{ 'bx-spin': loading }"></i> Refresh
      </button>
    </template>

    <div class="training-calendar-content">
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body">
          <div class="d-flex flex-wrap align-items-center gap-3 mb-3">
            <label class="me-2 mb-0">Year:</label>
            <select v-model="selectedYear" class="form-select form-select-sm" style="width: 110px;" @change="goToYear">
              <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
            </select>
            <div class="btn-group" role="group">
              <button type="button" class="btn btn-sm" :class="calendarView === 'dayGridMonth' ? 'btn-primary' : 'btn-outline-primary'" @click="setCalendarView('dayGridMonth')">Month</button>
              <button type="button" class="btn btn-sm" :class="calendarView === 'listMonth' ? 'btn-primary' : 'btn-outline-primary'" @click="setCalendarView('listMonth')">List</button>
            </div>
          </div>
          <p class="hr-text-muted hr-mb-0 hr-small">
            <i class="bx bx-info-circle"></i> Click a session to view venue, audience, and details. Sessions are created from <strong>Trainings Catalog</strong>.
          </p>
        </div>
      </div>

      <div class="hr-chart-card">
        <div class="hr-chart-body p-0 position-relative">
          <div v-if="calendarLoading" class="calendar-loading">
            <div class="hr-spinner"></div>
          </div>
          <div ref="calendarEl" class="training-fc"></div>
        </div>
      </div>
    </div>

    <div v-if="selectedEvent" class="hr-modal-overlay" @click.self="selectedEvent = null">
      <div class="hr-modal hr-modal-medium">
        <div class="hr-modal-header">
          <h3>{{ selectedEvent.title }}</h3>
          <button type="button" class="hr-modal-close" @click="selectedEvent = null">&times;</button>
        </div>
        <div class="hr-p-3">
          <p><strong>Dates:</strong> {{ formatRange(selectedEvent.start, selectedEvent.end) }}</p>
          <p v-if="selectedEvent.extendedProps.trainingCode"><strong>Code:</strong> {{ selectedEvent.extendedProps.trainingCode }}</p>
          <p v-if="selectedEvent.extendedProps.trainingType"><strong>Type:</strong> {{ selectedEvent.extendedProps.trainingType }}</p>
          <p v-if="selectedEvent.extendedProps.provider"><strong>Provider:</strong> {{ selectedEvent.extendedProps.provider }}</p>
          <p v-if="selectedEvent.extendedProps.venue"><strong>Venue:</strong> {{ selectedEvent.extendedProps.venue }}</p>
          <p><strong>Audience:</strong> {{ selectedEvent.extendedProps.audience || '—' }}</p>
          <p v-if="selectedEvent.extendedProps.notes" class="hr-mb-0"><strong>Notes:</strong> {{ selectedEvent.extendedProps.notes }}</p>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { Calendar } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import bootstrap5Plugin from "@fullcalendar/bootstrap5";

export default {
  name: "TrainingCalendar",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      calendarLoading: false,
      selectedYear: new Date().getFullYear(),
      calendarView: "dayGridMonth",
      calendar: null,
      events: [],
      selectedEvent: null
    };
  },
  computed: {
    yearOptions() {
      const y = new Date().getFullYear();
      return [y - 1, y, y + 1, y + 2];
    }
  },
  mounted() {
    this.initCalendar();
  },
  beforeUnmount() {
    if (this.calendar) {
      this.calendar.destroy();
      this.calendar = null;
    }
  },
  methods: {
    initCalendar() {
      if (!this.$refs.calendarEl) return;
      this.calendar = new Calendar(this.$refs.calendarEl, {
        plugins: [dayGridPlugin, listPlugin, interactionPlugin, bootstrap5Plugin],
        themeSystem: "bootstrap5",
        initialView: this.calendarView,
        headerToolbar: {
          left: "prev,next today",
          center: "title",
          right: ""
        },
        height: "auto",
        events: [],
        eventClick: (info) => {
          this.selectedEvent = info.event;
        },
        datesSet: (arg) => {
          this.fetchEvents(arg.startStr.slice(0, 10), arg.endStr.slice(0, 10));
        }
      });
      this.calendar.render();
    },
    setCalendarView(view) {
      this.calendarView = view;
      if (this.calendar) {
        this.calendar.changeView(view);
      }
    },
    goToYear() {
      if (this.calendar) {
        this.calendar.gotoDate(`${this.selectedYear}-01-01`);
      }
    },
    refreshData() {
      this.loading = true;
      if (this.calendar) {
        const view = this.calendar.view;
        this.fetchEvents(
          view.activeStart.toISOString().slice(0, 10),
          view.activeEnd.toISOString().slice(0, 10)
        );
      }
    },
    async fetchEvents(start, end) {
      this.calendarLoading = true;
      const data = new FormData();
      data.append("function", "getTrainingCalendarEvents");
      data.append("start", start);
      data.append("end", end);
      try {
        const res = await execute(data, authUrl);
        if (res.data?.success) {
          this.events = res.data.data?.events || [];
          if (this.calendar) {
            this.calendar.removeAllEvents();
            this.calendar.addEventSource(this.events);
          }
        }
      } catch (_) {
        this.events = [];
      } finally {
        this.calendarLoading = false;
        this.loading = false;
      }
    },
    formatRange(start, end) {
      const s = start ? new Date(start).toLocaleDateString() : "—";
      if (!end) return s;
      const endAdj = new Date(end);
      endAdj.setDate(endAdj.getDate() - 1);
      const e = endAdj.toLocaleDateString();
      return s === e ? s : `${s} – ${e}`;
    }
  }
};
</script>

<style scoped>
.training-calendar-content { padding: 0; }
.training-fc { padding: 16px; min-height: 520px; }
.calendar-loading {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}
.hr-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.hr-modal {
  background: #fff;
  border-radius: 16px;
  max-width: 96vw;
  max-height: 90vh;
  overflow-y: auto;
}
.hr-modal-medium { width: 480px; }
.hr-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
}
.hr-modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #64748b;
}
.hr-p-3 { padding: 20px; }
.hr-small { font-size: 0.875rem; }
</style>
